import React, { useState, useRef, useEffect } from "react";
import { FormGroup, Label, Input, InputGroup, InputGroupAddon } from "reactstrap";
import { IoSearchOutline } from "react-icons/io5";
import { FaChevronDown, FaChevronUp, FaTimes } from "react-icons/fa";

function Dropdown({
  options = [],
  placeholder,
  selectedDropdownValue,
  setSelectedDropdownValue,
  invalid,
  disabled = false,
  showEmptyOption = false,
  emptyOptionLabel = "None",
  toggleable = false
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const getLabel = (item) => (typeof item === "object" ? item.label : item);
  const getValue = (item) => (typeof item === "object" ? item.value : item);

  // Add empty option if enabled
  const allOptions = showEmptyOption 
    ? [{ value: "", label: emptyOptionLabel }, ...options]
    : options;

  const filteredOptions = allOptions.filter((item) => {
    const label = getLabel(item);
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const selectedOption = allOptions.find((opt) => getValue(opt) === selectedDropdownValue);

  const handleDropdownToggle = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };


  return (
    <FormGroup className="position-relative mb-0" ref={dropdownRef}>
      <InputGroup>
        <Input
          type="text"
          readOnly
          disabled={disabled}
          value={selectedOption ? getLabel(selectedOption) : ""}
          placeholder={placeholder}
          onClick={handleDropdownToggle}
          style={{ cursor: disabled ? "default" : "pointer", backgroundColor: disabled ? "#e9ecef" : "transparent" }}
          className={invalid ? "border-danger" : ""}
        />
        {toggleable && selectedOption && (
          <InputGroupAddon addonType="append">
            <span
              className="input-group-text"
              style={{
                cursor: "pointer",
                borderLeft: "none",
                borderRight: "none",
                backgroundColor: "#fff",
                color: "#adb5bd",
              }}
              title="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDropdownValue("");
                setOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#495057";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#adb5bd";
              }}
            >
              <FaTimes size={12} />
            </span>
          </InputGroupAddon>
        )}
        <InputGroupAddon addonType="append">
          <span
            className={invalid ? "input-group-text bg-danger border-danger" : "input-group-text bg-light"}
            style={{ cursor: disabled ? "default" : "pointer", borderLeft: "none", opacity: disabled ? 0.65 : 1 }}
            onClick={handleDropdownToggle}
          >
            {open ? <FaChevronUp className={invalid ? "text-white" : ""} /> : <FaChevronDown className={invalid ? "text-white" : ""} />}
          </span>
        </InputGroupAddon>
      </InputGroup>

      {open && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{ zIndex: 1000, top: "100%", marginTop: "4px" }}
        >
          {/* Search Bar */}
          <div className="border-bottom p-2">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <span className="input-group-text bg-light">
                  <IoSearchOutline />
                </span>
              </InputGroupAddon>
              <Input
                ref={searchInputRef}
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                onClick={(e) => e.stopPropagation()}
              />
            </InputGroup>
          </div>

          {/* Options List */}
          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((item) => {
                const value = getValue(item);
                const label = getLabel(item);
                const isSelected = selectedDropdownValue === value;

                return (
                  <div
                    key={value !== "" ? value : "empty-option"}
                    onClick={() => {
                      if (toggleable && isSelected) {
                        setSelectedDropdownValue("");
                      } else {
                        setSelectedDropdownValue(value === "" ? "" : value);
                      }
                      setOpen(false);
                    }}
                    className={`px-3 py-2 cursor-pointer d-flex align-items-center ${isSelected ? "bg-primary text-white" : ""}`}
                    style={{
                      cursor: "pointer",
                      gap: "8px",
                      backgroundColor: isSelected ? "#f0f0f0" : "",
                      transition: "background-color 0.2s",

                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = "#f0f0f0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = "";
                      }
                    }}
                  >
                    {toggleable && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        style={{ cursor: "pointer" }}
                      />
                    )}
                    {label}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-2 text-center text-muted">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </FormGroup>
  );
}

export default Dropdown;
